
import {
  getCompany as getCompanyApi,
  updateCompany as updateCompanyApi,
  requestCompanyDeletion as requestCompanyDeletionApi,
  deleteCompany as deleteCompanyApi,
} from "@/lib/api/clients/CompanyClient";
import { Company } from "@/types/Company";
import { getCookie, setCriticalCookie } from "@/lib/auth/cookies";
import { handleError, ServerError } from "@/lib/api/errors/ErrorHandler";
import type { LossesAck } from "@/lib/legal/dissolveLossesAck";

// Get a company by ID
export async function getCompany(
  fields: string,
): Promise<Company | ServerError> {
  const companyId = await getCookie("companyId");

  if (!companyId) {
    return handleError("Company ID is not set in cookies.");
  }

  try {
    const response = await getCompanyApi(companyId, fields);

    if (!response) {
      return handleError("Company not found or access denied.");
    }

    response.companyId = companyId; // Ensure companyId is set

    return response;
  } catch (error) {
    return handleError(error);
  }
}

// Update a company
export async function updateCompany(
  company: Company,
): Promise<void | ServerError> {

  try {
    await updateCompanyApi(company);
  } catch (error) {
    return handleError(error);
  }
}

// Request company deletion (sends OTP). PB-CDP-7: `lossesAck` is set exactly
// when the dialog showed a loss notice AND the user ticked the box under it.
export async function requestCompanyDeletion(
  lossesAck: LossesAck | null,
): Promise<void | ServerError> {
  const companyId = await getCookie("companyId");

  if (!companyId) {
    return handleError("Company ID is not set in cookies.");
  }

  try {
    await setCriticalCookie("otpResend", `${Date.now() + 99 * 1000}`);
    const response = await requestCompanyDeletionApi(companyId, lossesAck);
    return response;
  } catch (error) {
    return handleError(error);
  }
}

// Delete a company with OTP verification.
// F24 v5: backend now returns 202 + {status: "pending", pollUrl, retryAfterSeconds}.
// The caller (useVerificationForm) checks for `status === "pending"` and redirects
// to /user/delete-account/pending; the pending screen polls getDeletionStatus.
export async function deleteCompany(
  otp: string,
  // PB-CDP-7: the request's acknowledgement again — the confirm door
  // re-checks the losses and records it.
  lossesAck: LossesAck | null,
): Promise<{ status: "pending"; pollUrl: string; retryAfterSeconds: number } | ServerError> {
  const companyId = await getCookie("companyId");

  if (!companyId) {
    return handleError("Company ID is not set in cookies.");
  }

  if (!otp || otp.trim() === "") {
    return handleError("OTP is required for company deletion.");
  }

  try {
    const response = await deleteCompanyApi(companyId, otp, lossesAck);
    return response;
  } catch (error) {
    return handleError(error);
  }
}

// F24 v5: polling handler used by the /user/delete-account/pending screen.
export async function pollDeletionStatus(): Promise<
  | { status: "active" | "pending" | "completed" | "failed"; attempts?: number; retryAfterSeconds?: number; reason?: string; contactSupport?: boolean }
  | ServerError
> {
  const companyId = await getCookie("companyId");
  if (!companyId) {
    return handleError("Company ID is not set in cookies.");
  }
  try {
    const { getDeletionStatus } = await import("@/lib/api/clients/CompanyClient");
    return await getDeletionStatus(companyId);
  } catch (error) {
    // F24 v5 edge case: once the cascade hard-deletes the user row, the
    // next /deletion-status poll's auth check 401s — the JWT is valid but
    // the user it points at is gone. Treat that as terminal "completed"
    // so the pending page calls logout() + redirects, matching what the
    // v5 plan specified for the "JWT expiry during polling" branch.
    const status = (error as Error & { status?: number })?.status;
    if (status === 401) {
      return { status: "completed" };
    }
    return handleError(error);
  }
}
